<script lang="ts">
 import AdminNavigation from "./AdminNavigation.svelte";
 import ImpersonationBanner from "./ImpersonationBanner.svelte";
	import { Bell, User, Search, X, Sun, Moon, Monitor, Activity, Users, Building2, House, ShieldAlert } from 'lucide-svelte';
	import { user, logout } from '../stores/user';
	import { onMount, onDestroy, tick } from 'svelte';
	import { push } from 'svelte-spa-router';
	import { fade } from 'svelte/transition';
	import { unitPreference } from '../stores/preferences';
	import { activeAlarmCount } from '../stores/adminAlerts';
	import { clearSessionNavigation } from '../utils/sessionExpiry';
	let { children } = $props();

	let route = $state(window.location.hash);
	let impersonationEnded = $state(sessionStorage.getItem('saya.impersonationEnded') === 'true');
  sessionStorage.removeItem('saya.impersonationEnded');
	let currentTheme = $state('auto'); // 'light', 'dark', 'auto'
	let activeDark = $state(false);
	
	let searchQuery = $state('');
  let searchError = $state('');
  let searchGeneration = 0;
  let searchController: AbortController | undefined;
  function cancelSearch() {
    searchGeneration++;
    if (searchTimeout) clearTimeout(searchTimeout);
    searchController?.abort();
    searchResults = [];
    showResults = false;
    searchError = '';
    selectedIndex = -1;
  }
  onDestroy(cancelSearch);
	let searchResults = $state<any[]>([]);
	let showResults = $state(false);
	let selectedIndex = $state(-1);
	let searchTimeout: ReturnType<typeof setTimeout> | null = null;
	let searchContainerEl: HTMLDivElement;
	let compactSearchInputEl = $state<HTMLInputElement>();
	let compactSearchOpen = $state(false);

	async function toggleCompactSearch() {
		compactSearchOpen = !compactSearchOpen;
    if (!compactSearchOpen) { cancelSearch(); searchQuery = ''; }
		showResults = compactSearchOpen && searchResults.length > 0;
		if (compactSearchOpen) {
			await tick();
			compactSearchInputEl?.focus();
		}
	}

	function applyTheme(theme: string) {
		if (theme === 'auto') {
			activeDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
			document.documentElement.removeAttribute('data-theme');
			localStorage.removeItem('theme');
		} else {
			activeDark = theme === 'dark';
			document.documentElement.setAttribute('data-theme', theme);
			localStorage.setItem('theme', theme);
		}
		
		if (activeDark) {
			document.documentElement.classList.add('dark');
		} else {
			document.documentElement.classList.remove('dark');
		}
	}

	function cycleTheme() {
		const next = currentTheme === 'auto' ? 'dark' : (currentTheme === 'dark' ? 'light' : 'auto');
		currentTheme = next;
		applyTheme(next);
	}

	async function doSearch(q: string, run: number) {
    if (run !== searchGeneration) return;
    searchController = new AbortController();
    try {
      const res = await fetch(`/api/search?q=${encodeURIComponent(q)}&limit=8`, { signal: searchController.signal });
      if (!res.ok) throw new Error(res.status === 403 ? 'Access denied.' : 'Search unavailable. Please try again.');
      const data = await res.json();
      if (run !== searchGeneration) return;
      searchResults = data.results || [];
      showResults = searchResults.length > 0;
    } catch (e) {
      if (run === searchGeneration) searchError = e instanceof Error ? e.message : 'Search unavailable. Please try again.';
    }
  }

  function handleSearchInput(e: Event) {
    cancelSearch();
    const value = (e.target as HTMLInputElement).value.trim();
    const run = searchGeneration;
    if (value.length >= 2) searchTimeout = setTimeout(() => doSearch(value, run), 300);
  }

	function handleSearchKeydown(e: KeyboardEvent) {
		if (e.key === 'Escape') {
      cancelSearch();
			showResults = false;
			searchQuery = '';
			compactSearchOpen = false;
			return;
		}
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			selectedIndex = Math.min(selectedIndex + 1, searchResults.length - 1);
			return;
		}
		if (e.key === 'ArrowUp') {
			e.preventDefault();
			selectedIndex = Math.max(selectedIndex - 1, -1);
			return;
		}
		if (e.key === 'Enter') {
			e.preventDefault();
			if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
				navigateToResult(searchResults[selectedIndex]);
			} else if (searchResults.length > 0) {
				navigateToResult(searchResults[0]);
			}
			return;
		}
	}

	function navigateToResult(result: any) {
		cancelSearch();
    push(result.link);
		searchQuery = '';
		searchResults = [];
		showResults = false;
		selectedIndex = -1;
		compactSearchOpen = false;
	}

	async function handleLogout() {
    cancelSearch();
		clearSessionNavigation();
		await logout();
		push('/login');
	}

	function handleClickOutside(e: MouseEvent) {
		if (searchContainerEl && !searchContainerEl.contains(e.target as Node)) {
      cancelSearch();
      searchQuery = '';
			showResults = false;
			compactSearchOpen = false;
		}
	}

	function getCategoryIcon(category: string) {
		return category; // We'll use inline logic in the template
	}

	onMount(() => {
		currentTheme = localStorage.getItem('theme') || 'auto';
		applyTheme(currentTheme);

		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
		const onThemeChange = () => {
			if (currentTheme === 'auto') {
				applyTheme('auto');
			}
		};
    mediaQuery.addEventListener('change', onThemeChange);

		const onHashChange = () => { route = window.location.hash; };
		window.addEventListener('hashchange', onHashChange);

		document.addEventListener('click', handleClickOutside);
		return () => { document.removeEventListener('click', handleClickOutside); mediaQuery.removeEventListener('change', onThemeChange); window.removeEventListener('hashchange', onHashChange); };
	});

	// The unit toggle only matters on pages that display measured values.
	const currentPath = $derived(route.replace(/^#/, '').split('?')[0] || '/');
	const showUnitToggle = $derived(
		currentPath === '/' ||
		currentPath === '/admin' ||
		currentPath.startsWith('/devices') ||
		currentPath.startsWith('/properties') ||
		currentPath.startsWith('/admin/properties') ||
		currentPath.startsWith('/admin/devices')
	);
</script>

<div class="min-h-screen flex flex-col font-sans bg-base-100 relative overflow-hidden transition-colors duration-300">
	<!-- Top Navigation Bar -->
	<header class="sticky top-0 z-50 glass-nav">
    <ImpersonationBanner />
    {#if impersonationEnded}
      <div role="status" class="px-4 py-2 bg-base-200 text-sm">Impersonation ended. You are back in your own account. <button class="btn btn-xs btn-ghost" onclick={() => impersonationEnded = false}>Dismiss</button></div>
    {/if}
		<div class="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 w-full">
			<div class="flex items-center justify-between h-16">
				<!-- Brand & Nav Links -->
				<div class="flex items-center gap-10">
					<a href="#/" aria-label="Saya Intelligence" class="flex items-baseline gap-0.5 group">
						<span class="text-3xl sm:text-4xl font-black tracking-tighter text-base-content group-hover:text-primary transition-colors">saya</span>
						<div class="flex items-baseline mb-1">
							<span class="text-xl sm:text-2xl font-black text-primary">i</span>
							<span class="hidden sm:inline text-[9px] font-black uppercase tracking-[0.2em] text-base-content/50 ml-0.5 group-hover:text-base-content/70 transition-colors">ntelligence</span>
						</div>
					</a>
					
					<!-- Desktop Navigation -->
					<nav aria-label="Main" class="hidden xl:flex items-center gap-1 h-16">
						<a 
							href="#/" 
							class="h-16 inline-flex items-center text-xs font-semibold px-3.5 border-b-2 transition-all {currentPath === '/' ? 'border-primary text-primary font-bold' : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-content/20'}"
						>Overview</a>
						<a 
							href="#/devices" 
							class="h-16 inline-flex items-center text-xs font-semibold px-3.5 border-b-2 transition-all {currentPath.startsWith('/devices') ? 'border-primary text-primary font-bold' : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-content/20'}"
						>Devices</a>
						{#if $user?.user_type === 'system_admin' || $user?.user_type === 'property_manager'}
							<a 
								href={$user?.user_type === 'system_admin' ? '#/admin/properties' : '#/properties'} 
								class="h-16 inline-flex items-center text-xs font-semibold px-3.5 border-b-2 transition-all {currentPath.startsWith('/admin/properties') || currentPath.startsWith('/properties') ? 'border-primary text-primary font-bold' : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-content/20'}"
							>Properties</a>
							<a 
								href={$user?.user_type === 'system_admin' ? '#/admin/alerts' : '#/alerts'} 
								class="relative h-16 inline-flex items-center text-xs font-semibold px-3.5 border-b-2 transition-all {currentPath.startsWith('/admin/alerts') || currentPath.startsWith('/alerts') ? 'border-primary text-primary font-bold' : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-content/20'}"
							>
								Alerts
								{#if $activeAlarmCount}<span class="ml-1.5 w-2 h-2 rounded-full bg-error" aria-hidden="true"></span>{/if}
							</a>
						{/if}
						{#if $user?.user_type !== 'installer'}
							<a 
								href="#/copilot" 
								class="h-16 inline-flex items-center text-xs font-semibold px-3.5 border-b-2 transition-all {currentPath.startsWith('/copilot') || currentPath.startsWith('/chat') ? 'border-primary text-primary font-bold' : 'border-transparent text-base-content/70 hover:text-base-content hover:border-base-content/20'}"
							>Copilot</a>
						{/if}
					</nav>
				</div>

				<!-- Right Actions -->
				<div class="flex items-center gap-1 sm:gap-4">
					<!-- Global Search -->
					<div class="relative" bind:this={searchContainerEl}>
						<div class="hidden lg:flex items-center bg-base-content/[0.03] rounded-2xl px-4 py-2 border border-base-content/10 focus-within:border-primary/50 focus-within:bg-base-content/[0.06] transition-all w-48 xl:w-64">
							<Search class="w-3.5 h-3.5 text-base-content/50 mr-2" />
							<input
								type="text"
								id="global-search"
								name="global-search"
								bind:value={searchQuery}
								oninput={handleSearchInput}
								onkeydown={handleSearchKeydown}
								onfocus={() => { if (searchResults.length > 0) showResults = true; }}
								placeholder="Explore fleet..."
								class="bg-transparent border-none outline-none text-xs w-full placeholder-base-content/50 focus:ring-0 text-base-content/80"
							/>
						</div>
						<button
							type="button"
							class="lg:hidden btn btn-ghost btn-circle btn-sm text-base-content/60 hover:text-base-content"
							aria-label={compactSearchOpen ? 'Close fleet search' : 'Search fleet'}
							aria-expanded={compactSearchOpen}
							onclick={toggleCompactSearch}
						>
							{#if compactSearchOpen}<X class="w-4 h-4" />{:else}<Search class="w-4 h-4" />{/if}
						</button>

						{#if compactSearchOpen}
							<div class="lg:hidden absolute top-full mt-3 right-0 w-[min(20rem,calc(100vw-2rem))] menu-surface rounded-2xl z-50 overflow-hidden border border-base-content/10" in:fade={{ duration: 120 }}>
								<div class="flex items-center px-4 py-3 border-b border-base-content/10 focus-within:bg-base-content/[0.04]">
									<Search class="w-4 h-4 text-base-content/50 mr-2" />
									<input
										type="text"
										id="global-search-compact"
										name="global-search-compact"
										aria-label="Search fleet"
										bind:this={compactSearchInputEl}
										bind:value={searchQuery}
										oninput={handleSearchInput}
										onkeydown={handleSearchKeydown}
										onfocus={() => { if (searchResults.length > 0) showResults = true; }}
										placeholder="Explore fleet..."
										class="bg-transparent border-none outline-none text-sm w-full placeholder-base-content/50 focus:ring-0 text-base-content/80"
									/>
								</div>
								{#if showResults}
									<div class="max-h-80 overflow-y-auto p-2">
										{#each searchResults as result, i}
											<button
												class="w-full text-left px-4 py-3 flex items-center gap-3 rounded-2xl transition-all {selectedIndex === i ? 'bg-base-content/10' : 'hover:bg-base-content/5'}"
												onclick={() => navigateToResult(result)}
												onmouseenter={() => selectedIndex = i}
											>
												<div class="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border border-base-content/10 {result.category === 'device' ? 'bg-cyan-500/10 text-cyan-500' : result.category === 'user' ? 'bg-blue-500/10 text-blue-500' : (result.category === 'company' || result.category === 'organization') ? 'bg-indigo-500/10 text-indigo-500' : 'bg-emerald-500/10 text-emerald-500'}">
													{#if result.category === 'device'}<Activity class="w-4 h-4" />{:else if result.category === 'user'}<Users class="w-4 h-4" />{:else if (result.category === 'company' || result.category === 'organization')}<Building2 class="w-4 h-4" />{:else}<House class="w-4 h-4" />{/if}
												</div>
												<div class="min-w-0 flex-1"><div class="text-xs font-black text-base-content truncate">{result.label}</div><div class="text-[10px] text-base-content/70 font-bold truncate uppercase tracking-tight">{result.subtitle}</div></div>
											</button>
										{/each}
									</div>
								{/if}
							</div>
						{/if}

						{#if searchError}<p role="alert" class="text-xs text-error mt-2">{searchError}</p>{/if}
            <!-- Search Results Dropdown -->
						{#if showResults && !compactSearchOpen}
							<div class="absolute top-full mt-3 right-0 w-80 menu-surface rounded-3xl z-50 overflow-hidden border border-base-content/10" in:fade={{ duration: 150 }}>
								<div class="max-h-80 overflow-y-auto p-2">
									{#each searchResults as result, i}
										<button
											class="w-full text-left px-4 py-3 flex items-center gap-3 rounded-2xl transition-all {selectedIndex === i ? 'bg-base-content/10' : 'hover:bg-base-content/5'}"
											onclick={() => navigateToResult(result)}
											onmouseenter={() => selectedIndex = i}
										>
											<div class="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border border-base-content/10 {
												result.category === 'device' ? 'bg-cyan-500/10 text-cyan-500 shadow-sm' :
												result.category === 'user' ? 'bg-blue-500/10 text-blue-500 shadow-sm' :
												(result.category === 'company' || result.category === 'organization') ? 'bg-indigo-500/10 text-indigo-500 shadow-sm' :
												'bg-emerald-500/10 text-emerald-500 shadow-sm'
											}">
												{#if result.category === 'device'} <Activity class="w-4 h-4" />
												{:else if result.category === 'user'} <Users class="w-4 h-4" />
												{:else if (result.category === 'company' || result.category === 'organization')} <Building2 class="w-4 h-4" />
												{:else} <House class="w-4 h-4" />
												{/if}
											</div>
											<div class="min-w-0 flex-1">
												<div class="text-xs font-black text-base-content truncate">{result.label}</div>
												<div class="text-[10px] text-base-content/70 font-bold truncate uppercase tracking-tight">{result.subtitle}</div>
											</div>
										</button>
									{/each}
								</div>
								<div class="px-5 py-2.5 text-[9px] font-black text-base-content/50 uppercase tracking-widest border-t border-base-content/5 bg-base-content/[0.03]">
									Search command center
								</div>
							</div>
						{/if}
					</div>

					{#if $user?.user_type === 'system_admin'}
					<div class="dropdown dropdown-end">
						<div tabindex="0" role="button" aria-label="Administration menu" class="btn btn-ghost btn-sm gap-1.5 text-base-content/60 hover:text-base-content">
							<ShieldAlert class="w-4 h-4" />
							<span class="hidden sm:inline text-[10px] font-black uppercase tracking-widest">Admin</span>
						</div>
						<ul class="mt-4 z-[1] p-3 menu menu-sm dropdown-content menu-surface rounded-3xl w-56 border border-base-content/10" in:fade={{ duration: 150 }}>
							<li class="menu-title px-4 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-base-content/40">Administration</li>
							<AdminNavigation variant="menu" />
							<li><hr class="border-base-content/5 my-2" /></li>
							<li><a href="#/installer/jobs" class="rounded-xl px-4 py-2 hover:bg-primary/10 font-bold text-base-content/70 hover:text-primary transition-colors">Installation jobs</a></li>
						</ul>
					</div>
					{/if}
					<div class="flex items-center gap-1">
						{#if showUnitToggle}
						<button 
							onclick={() => $unitPreference = $unitPreference === 'metric' ? 'imperial' : 'metric'} 
							class="btn btn-ghost btn-xs rounded-full font-black text-[10px] uppercase tracking-wider text-base-content/60 hover:text-base-content hover:bg-base-content/10 mr-1 px-3"
							title="Toggle Units"
						>
							{$unitPreference === 'metric' ? 'Metric' : 'Imperial'}
						</button>
						{/if}
						<button onclick={cycleTheme} class="btn btn-ghost btn-circle btn-sm text-base-content/60 hover:text-base-content" aria-label="Toggle Theme">
							{#if currentTheme === 'auto'} <Monitor class="w-4 h-4" />
							{:else if currentTheme === 'dark'} <Moon class="w-4 h-4" />
							{:else} <Sun class="w-4 h-4" />
							{/if}
						</button>

					</div>

					<div class="dropdown dropdown-end">
						<div tabindex="0" role="button" aria-label="Account menu" class="btn btn-ghost btn-circle avatar ml-1 p-0.5 border border-base-content/10 hover:border-primary/30 transition-all bg-base-content/[0.03]">
							<div class="w-8 h-8 rounded-full flex items-center justify-center bg-primary/10 text-primary">
								<User class="w-4.5 h-4.5" />
							</div>
						</div>
						<ul class="mt-4 z-[1] p-3 menu menu-sm dropdown-content menu-surface rounded-3xl w-52 border border-base-content/10" in:fade={{ duration: 150 }}>
							<li class="menu-title px-4 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-base-content/40">Identity</li>
							<li><a href="#/profile" class="rounded-xl px-4 py-2 hover:bg-primary/10 font-bold text-base-content/70 hover:text-primary transition-colors">My Profile</a></li>
							<li><a href="#/settings" class="rounded-xl px-4 py-2 hover:bg-primary/10 font-bold text-base-content/70 hover:text-primary transition-colors">Preferences</a></li>
							<li><hr class="border-base-content/5 my-2" /></li>
							<li><button onclick={handleLogout} class="rounded-xl px-4 py-2 text-error font-black hover:bg-error/10 transition-colors">Terminate Session</button></li>
						</ul>
					</div>
				</div>
			</div>
			
			<!-- Compact navigation stays visible through tablet widths with accessible touch targets -->
			<nav aria-label="Main" class="xl:hidden flex items-center gap-1.5 py-2 border-t border-base-content/5 overflow-x-auto no-scrollbar">
				<a 
					href="#/" 
					class="min-h-[38px] inline-flex items-center text-xs font-semibold px-3.5 py-2 rounded-xl whitespace-nowrap transition-all {currentPath === '/' ? 'bg-primary/10 text-primary font-bold shadow-xs' : 'text-base-content/70 hover:text-base-content hover:bg-base-content/5'}"
				>Overview</a>
				<a 
					href="#/devices" 
					class="min-h-[38px] inline-flex items-center text-xs font-semibold px-3.5 py-2 rounded-xl whitespace-nowrap transition-all {currentPath.startsWith('/devices') ? 'bg-primary/10 text-primary font-bold shadow-xs' : 'text-base-content/70 hover:text-base-content hover:bg-base-content/5'}"
				>Devices</a>
				{#if $user?.user_type === 'system_admin' || $user?.user_type === 'property_manager'}
					<a 
						href={$user?.user_type === 'system_admin' ? '#/admin/properties' : '#/properties'} 
						class="min-h-[38px] inline-flex items-center text-xs font-semibold px-3.5 py-2 rounded-xl whitespace-nowrap transition-all {currentPath.startsWith('/admin/properties') || currentPath.startsWith('/properties') ? 'bg-primary/10 text-primary font-bold shadow-xs' : 'text-base-content/70 hover:text-base-content hover:bg-base-content/5'}"
					>Properties</a>
					<a 
						href={$user?.user_type === 'system_admin' ? '#/admin/alerts' : '#/alerts'} 
						class="relative min-h-[38px] inline-flex items-center text-xs font-semibold px-3.5 py-2 rounded-xl whitespace-nowrap transition-all {currentPath.startsWith('/admin/alerts') || currentPath.startsWith('/alerts') ? 'bg-primary/10 text-primary font-bold shadow-xs' : 'text-base-content/70 hover:text-base-content hover:bg-base-content/5'}"
					>
						Alerts
						{#if $activeAlarmCount}<span class="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-error" aria-hidden="true"></span>{/if}
					</a>
				{/if}
				{#if $user?.user_type !== 'installer'}
					<a 
						href="#/copilot" 
						class="min-h-[38px] inline-flex items-center text-xs font-semibold px-3.5 py-2 rounded-xl whitespace-nowrap transition-all {currentPath.startsWith('/copilot') || currentPath.startsWith('/chat') ? 'bg-primary/10 text-primary font-bold shadow-xs' : 'text-base-content/70 hover:text-base-content hover:bg-base-content/5'}"
					>Copilot</a>
				{/if}
			</nav>
		</div>
	</header>

	<!-- Page content -->
	<main class="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto">
		<div class="max-w-7xl mx-auto w-full h-full flex flex-col">
			{@render children()}
		</div>
	</main>
</div>
